import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { getLocationSyncPreview, commitLocationSync } from './api';

// "Update Location List" flow (spec §5.3). Opens a modal that previews the
// diff first -- adds (locations active under brand, no row yet) and removes
// (rows whose location is non-active or deleted). AM confirms before writes
// happen; commit generates new rows and soft-deletes removed ones. Existing
// rows are never touched -- no content overwrite, no refresh.

type Props = {
  rolloutId: string;
  onClose: () => void;
};

export function UpdateLocationListModal({ rolloutId, onClose }: Props) {
  const qc = useQueryClient();
  const preview = useQuery({
    queryKey: ['rollout', rolloutId, 'location-sync-preview'],
    queryFn: () => getLocationSyncPreview(rolloutId),
    staleTime: 0,
  });

  const commit = useMutation({
    mutationFn: () => commitLocationSync(rolloutId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['rollout', rolloutId] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-2xl rounded-md bg-white shadow-xl">
        <div className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Update location list</h2>
          <p className="text-sm text-slate-600">
            Existing row content is never touched. Only new rows are generated and removed
            locations are soft-deleted.
          </p>

          {preview.isLoading ? (
            <div className="py-6 flex justify-center">
              <Spinner />
            </div>
          ) : preview.error ? (
            <p className="text-sm text-red-600">
              Failed to load preview: {(preview.error as Error).message}
            </p>
          ) : preview.data ? (
            <div className="space-y-4">
              <Section
                title={`Locations to add: ${preview.data.toAdd.length}`}
                color="emerald"
                items={preview.data.toAdd.map((a) => ({ id: a.accountId, label: a.accountName }))}
                emptyHint="No new locations to add."
              />
              <Section
                title={`Locations to remove: ${preview.data.toSoftDelete.length}`}
                color="amber"
                items={preview.data.toSoftDelete.map((s) => ({
                  id: s.accountCampaignId,
                  label: `${s.locationName} (${s.reason === 'account_not_active' ? 'non-active' : 'deleted'})`,
                }))}
                emptyHint="No rows to soft-delete."
              />
            </div>
          ) : null}

          {commit.error && (
            <p className="text-sm text-red-600">
              Commit failed: {(commit.error as Error).message}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => commit.mutate()}
              disabled={
                preview.isLoading ||
                commit.isPending ||
                !preview.data ||
                (preview.data.toAdd.length === 0 && preview.data.toSoftDelete.length === 0)
              }
            >
              {commit.isPending ? 'Committing…' : 'Confirm & commit'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  color,
  items,
  emptyHint,
}: {
  title: string;
  color: 'emerald' | 'amber';
  items: Array<{ id: string; label: string }>;
  emptyHint: string;
}) {
  const border = color === 'emerald' ? 'border-emerald-300' : 'border-amber-300';
  const head = color === 'emerald' ? 'text-emerald-800' : 'text-amber-800';
  return (
    <div className={`rounded-md border ${border} p-3`}>
      <p className={`text-sm font-medium ${head}`}>{title}</p>
      {items.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500">{emptyHint}</p>
      ) : (
        <ul className="mt-1 max-h-40 overflow-auto text-xs text-slate-700 space-y-0.5">
          {items.map((it) => (
            <li key={it.id}>• {it.label}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
